import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/** Buy buttons that must be locked while a variant change is in flight. */
const BUY_BUTTON_SELECTOR = [
  'button[name="add"]',
  'button.add-to-cart-button',
  '[data-testid="standalone-add-to-cart"]',
  'add-to-cart-component button[type="submit"]',
  '.shopify-payment-button__button',
].join(', ');

/** Nearest container that holds both this picker and its buy buttons. */
const BUY_BUTTON_SCOPE_SELECTOR = [
  'quick-add-dialog',
  'product-card',
  'featured-product-information',
  'product-information',
  '.shopify-section',
].join(', ');

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {boolean} */
  #isNormalizingSelection = false;

  /** @type {number} Number of variant requests currently in flight. */
  #pendingVariantRequests = 0;

  /** @type {Element | Document | undefined} */
  #buyButtonScope;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /**
   * Blocks keyboard and mouse activation while the buy buttons are locked.
   * `pointer-events: none` alone would still let Enter/Space through.
   * @param {Event} event
   */
  #blockBusyBuyButtonClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest(BUY_BUTTON_SELECTOR) : null;

    if (target?.classList.contains('is-variant-loading')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    this.#buyButtonScope = this.getBuyButtonScope();
    this.#buyButtonScope.addEventListener('click', this.#blockBusyBuyButtonClick, true);

    this.normalizeOptionGroups();
    this.syncCurrentVariantUrl();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#buyButtonScope?.removeEventListener('click', this.#blockBusyBuyButtonClick, true);

    // Safety net: never leave buttons locked if the picker is torn down mid-request.
    this.#pendingVariantRequests = 0;
    this.#applyBuyButtonsBusy(false);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.normalizeOptionGroups(this.getChangedOptionIndex(event.target, this.optionGroups));
    const resolvedVariant = this.currentResolvedVariant;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', connectedProductUrl = '' } = selectedOption.dataset;
    const variantId = resolvedVariant?.id != null ? `${resolvedVariant.id}` : selectedOption.dataset.variantId || '';
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl, isOnProductPage },
    });
  }

  /**
   * Resolves the nearest container that holds both this picker and its buy buttons.
   * Falls back to the document so a picker rendered outside a known wrapper still works.
   * @returns {Element | Document}
   */
  getBuyButtonScope() {
    return this.closest(BUY_BUTTON_SCOPE_SELECTOR) ?? document;
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  getBuyButtons() {
    return /** @type {HTMLButtonElement[]} */ (
      Array.from(this.getBuyButtonScope().querySelectorAll(BUY_BUTTON_SELECTOR))
    );
  }

  /**
   * Locks or unlocks the buy buttons.
   * @param {boolean} isBusy
   */
  #applyBuyButtonsBusy(isBusy) {
    for (const button of this.getBuyButtons()) {
      if (isBusy) {
        // Snapshot the server-rendered availability once, so a sold-out button stays sold out.
        if (!button.hasAttribute('data-variant-loading')) {
          button.setAttribute('data-variant-loading', button.disabled ? 'disabled' : 'enabled');
        }

        button.classList.add('is-variant-loading');
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        continue;
      }

      const snapshot = button.getAttribute('data-variant-loading');

      button.classList.remove('is-variant-loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('data-variant-loading');

      // A missing snapshot means morph re-rendered this button, so Liquid already
      // set the correct availability for the new variant. Leave it alone.
      if (snapshot) button.disabled = snapshot === 'disabled';
    }
  }

  #lockBuyButtons() {
    this.#pendingVariantRequests += 1;
    this.#applyBuyButtonsBusy(true);
  }

  /** Re-applies the lock after a morph has stripped our attributes. */
  #refreshBuyButtonsBusy() {
    if (this.#pendingVariantRequests > 0) this.#applyBuyButtonsBusy(true);
  }

  #unlockBuyButtons() {
    this.#pendingVariantRequests = Math.max(0, this.#pendingVariantRequests - 1);
    if (this.#pendingVariantRequests === 0) this.#applyBuyButtonsBusy(false);
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string, isOnProductPage?: boolean }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '', isOnProductPage = false } = detail;

    // Lock the buy buttons for the whole round trip, before anything can be added to cart.
    this.#lockBuyButtons();

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // The morph may have replaced the buy buttons, which drops our lock attributes.
        this.#refreshBuyButtonsBusy();

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            this.syncVariantHistory({
              isOnProductPage,
              variantId: variantData.id != null ? `${variantData.id}` : '',
              productUrl:
                html.querySelector('variant-picker')?.dataset?.productUrl || connectedProductUrl || this.dataset.productUrl,
            });

            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();
            const productViewVariant = productViewAttr && JSON.parse(productViewAttr)?.product?.selectedVariant;

            deferredEventPromise.resolve({
              variant: productViewVariant ?? variantData,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          this.#pendingRequestUrl = undefined;
          return;
        } else {
          deferredEventPromise.reject(error);
          console.error(error);
        }
      })
      .finally(() => {
        // Let the ProductSelectEvent listeners (price, buy buttons, inventory) finish
        // their own morphing before we hand control back to the customer.
        yieldToMainThread().then(() => this.#unlockBuyButtons());
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();
    this.normalizeOptionGroups();
    this.syncCurrentVariantUrl();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Filters each option group by the currently selected prefix and keeps the selection on a valid available combination.
   * This works by option position, so it is safe for merged values like "Kids X-Small" and for future products.
   * @param {number} [startIndex=0]
   */
  normalizeOptionGroups(startIndex = 0) {
    const optionGroups = this.optionGroups;
    const variants = this.variantList.filter((variant) => variant?.available && Array.isArray(variant.options));

    if (!optionGroups.length || !variants.length) return;

    for (let index = Math.max(0, startIndex); index < optionGroups.length; index += 1) {
      const selectedValues = this.getSelectedValuesByOptionIndex(optionGroups);
      const optionIndex = this.getOptionIndex(optionGroups[index]);
      const allowedValues = this.getAllowedValuesForIndex(variants, selectedValues, optionIndex);

      if (!allowedValues.length) {
        const fallbackVariant = this.getFallbackVariantForIndex(variants, selectedValues, optionIndex);
        if (!fallbackVariant?.options) {
          continue;
        }

        for (let fallbackIndex = index; fallbackIndex < optionGroups.length; fallbackIndex += 1) {
          const fallbackOptionIndex = this.getOptionIndex(optionGroups[fallbackIndex]);
          const fallbackAllowedValues = this.getAllowedValuesForIndex(
            variants,
            this.getSelectedValuesByOptionIndex(optionGroups),
            fallbackOptionIndex
          );

          this.applyAllowedValues(optionGroups[fallbackIndex], fallbackAllowedValues);
          this.setOptionGroupValue(optionGroups[fallbackIndex], fallbackVariant.options[fallbackOptionIndex] || '');
        }

        break;
      }

      this.applyAllowedValues(optionGroups[index], allowedValues);

      const currentValue = this.getOptionGroupValue(optionGroups[index]);
      if (!allowedValues.includes(currentValue)) {
        this.setOptionGroupValue(optionGroups[index], allowedValues[0] || '');
      }
    }
  }

  /**
   * Finds the first available variant that matches the valid prefix before the current option index.
   * @param {any[]} variants
   * @param {string[]} selectedValues
   * @param {number} optionIndex
   * @returns {any | undefined}
   */
  getFallbackVariantForIndex(variants, selectedValues, optionIndex) {
    return variants.find((variant) => {
      for (let index = 0; index < optionIndex; index += 1) {
        if (selectedValues[index] && variant.options[index] !== selectedValues[index]) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * @param {any[]} variants
   * @param {string[]} selectedValues
   * @param {number} optionIndex
   * @returns {string[]}
   */
  getAllowedValuesForIndex(variants, selectedValues, optionIndex) {
    const allowedValues = new Set();

    for (const variant of variants) {
      if (optionIndex === 0) {
        if (variant.options[optionIndex]) {
          allowedValues.add(variant.options[optionIndex]);
        }
        continue;
      }

      let matchesPrefix = true;

      for (const [selectedIndex, selectedValue] of Object.entries(selectedValues)) {
        const numericIndex = Number.parseInt(selectedIndex, 10);
        if (numericIndex >= optionIndex) {
          continue;
        }

        if (selectedValue && variant.options[numericIndex] !== selectedValue) {
          matchesPrefix = false;
          break;
        }
      }

      if (matchesPrefix && variant.options[optionIndex]) {
        allowedValues.add(variant.options[optionIndex]);
      }
    }

    return [...allowedValues];
  }

  /**
   * @param {Element} optionGroup
   * @param {string[]} allowedValues
   */
  applyAllowedValues(optionGroup, allowedValues) {
    const allowedSet = new Set(allowedValues);
    const optionGroups = this.optionGroups;
    const optionGroupIndex = optionGroups.indexOf(optionGroup);

    if (optionGroup instanceof HTMLFieldSetElement) {
      for (const input of optionGroup.querySelectorAll('input[type="radio"]')) {
        if (!(input instanceof HTMLInputElement)) continue;

        const isAllowed = optionGroupIndex === 0 ? true : allowedSet.has(input.value);
        const label = input.closest('label');

        input.disabled = !isAllowed;
        input.dataset.optionAvailable = isAllowed ? 'true' : 'false';

        if (label instanceof HTMLElement) {
          label.hidden = !isAllowed;
          label.style.display = isAllowed ? '' : 'none';
        }
      }

      return;
    }

    if (optionGroup instanceof HTMLSelectElement) {
      for (const option of optionGroup.options) {
        const isAllowed = optionGroupIndex === 0 ? true : allowedSet.has(option.value);
        option.hidden = !isAllowed;
        option.disabled = !isAllowed;
      }
    }
  }

  /**
   * @returns {Element[]}
   */
  get optionGroups() {
    return Array.from(this.querySelectorAll('fieldset[ref="fieldsets[]"], select.variant-option__select'));
  }

  /**
   * @param {HTMLElement} target
   * @param {Element[]} optionGroups
   * @returns {number}
   */
  getChangedOptionIndex(target, optionGroups) {
    if (target instanceof HTMLInputElement) {
      return Number.parseInt(target.dataset.fieldsetIndex || '', 10);
    }

    if (target instanceof HTMLSelectElement) {
      return optionGroups.indexOf(target);
    }

    return -1;
  }

  /**
   * @param {Element} optionGroup
   * @returns {number}
   */
  getOptionIndex(optionGroup) {
    const sourceElement =
      optionGroup instanceof HTMLFieldSetElement
        ? optionGroup.querySelector('input[data-input-id]')
        : optionGroup instanceof HTMLSelectElement
        ? optionGroup.querySelector('option[data-input-id]')
        : null;

    if (!(sourceElement instanceof HTMLElement)) {
      return -1;
    }

    const inputId = sourceElement.dataset.inputId || '';
    const [optionPosition] = inputId.split('-');
    const parsedIndex = Number.parseInt(optionPosition || '', 10);

    return Number.isNaN(parsedIndex) ? -1 : parsedIndex - 1;
  }

  /**
   * @param {Element[]} optionGroups
   * @returns {Record<number, string>}
   */
  getSelectedValuesByOptionIndex(optionGroups) {
    return optionGroups.reduce((accumulator, group) => {
      const optionIndex = this.getOptionIndex(group);
      if (optionIndex === -1) {
        return accumulator;
      }

      accumulator[optionIndex] = this.getOptionGroupValue(group);
      return accumulator;
    }, /** @type {Record<number, string>} */ ({}));
  }

  /**
   * @param {Element} optionGroup
   * @returns {string}
   */
  getOptionGroupValue(optionGroup) {
    if (optionGroup instanceof HTMLFieldSetElement) {
      const checkedInput = optionGroup.querySelector('input:checked');
      return checkedInput instanceof HTMLInputElement ? checkedInput.value : '';
    }

    if (optionGroup instanceof HTMLSelectElement) {
      return optionGroup.value;
    }

    return '';
  }

  /**
   * @param {Element} optionGroup
   * @param {string} value
   */
  setOptionGroupValue(optionGroup, value) {
    if (!value) return;

    if (optionGroup instanceof HTMLFieldSetElement) {
      const matchingInput = Array.from(optionGroup.querySelectorAll('input[type="radio"]')).find(
        (input) => input instanceof HTMLInputElement && input.value === value
      );

      if (matchingInput instanceof HTMLInputElement && !matchingInput.checked) {
        this.updateSelectedOption(matchingInput);
      }

      return;
    }

    if (optionGroup instanceof HTMLSelectElement) {
      const matchingOption = Array.from(optionGroup.options).find((option) => option.value === value);
      if (matchingOption && optionGroup.value !== value) {
        optionGroup.value = value;
        this.updateSelectedOption(optionGroup);
      }
    }
  }

  /**
   * Keeps the product page URL in sync with the live checked variant after any picker normalization.
   * This covers the first-click case where Shopify rerenders and the picker auto-selects the first valid option.
   */
  syncCurrentVariantUrl() {
    if (this.dataset.templateProductMatch !== 'true') return;

    const variantId = this.currentVariantId;

    if (!variantId) return;
    this.syncVariantHistory({ isOnProductPage: true, variantId, productUrl: this.dataset.productUrl });
  }

  /**
   * Syncs the browser URL to the resolved live variant.
   * @param {{ isOnProductPage?: boolean, variantId?: string, productUrl?: string }} options
   */
  syncVariantHistory({ isOnProductPage = false, variantId = '', productUrl = '' } = {}) {
    if (!isOnProductPage || !variantId) return;

    const url = new URL(window.location.href);
    const normalizedProductUrl = productUrl ? productUrl.split('?')[0] : '';

    if (normalizedProductUrl) {
      url.pathname = normalizedProductUrl;
    }

    url.searchParams.set('variant', variantId);

    if (url.href === window.location.href) {
      return;
    }

    yieldToMainThread().then(() => {
      history.replaceState({}, '', url.toString());
    });
  }

  /**
   * Gets the currently resolved variant ID from the picker JSON, with a fallback to checked inputs/options.
   * @returns {string | undefined}
   */
  get currentVariantId() {
    const resolvedVariant = this.currentResolvedVariant;
    if (resolvedVariant?.id != null && `${resolvedVariant.id}` !== '') {
      return `${resolvedVariant.id}`;
    }

    const variantJson = this.querySelector('script[type="application/json"]');
    const textContent = variantJson?.textContent?.trim();

    if (textContent) {
      try {
        const parsed = JSON.parse(textContent);
        const id = parsed?.id;
        if (id != null && `${id}` !== '') {
          return `${id}`;
        }
      } catch (error) {
        console.warn('[variant-picker] Failed to parse current variant JSON', error);
      }
    }

    const selectedVariantOption = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).find((option) => option instanceof HTMLElement && option.dataset.variantId);

    if (selectedVariantOption instanceof HTMLElement) {
      return selectedVariantOption.dataset.variantId;
    }

    return undefined;
  }

  /**
   * @returns {any[]}
   */
  get variantList() {
    const variantListScript = this.querySelector('script[data-variant-list]');
    const textContent = variantListScript?.textContent?.trim();

    if (!textContent) return [];

    try {
      const parsed = JSON.parse(textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[variant-picker] Failed to parse variant list JSON', error);
      return [];
    }
  }

  /**
   * @returns {any | undefined}
   */
  get currentResolvedVariant() {
    const selectedValues = this.getSelectedValuesByOptionIndex(this.optionGroups);

    return this.variantList.find((variant) => {
      if (!variant?.available || !Array.isArray(variant.options)) {
        return false;
      }

      return Object.entries(selectedValues).every(([index, value]) => {
        const numericIndex = Number.parseInt(index, 10);
        return value && variant.options[numericIndex] === value;
      });
    });
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}